import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Bell, Plus, Compass, Ticket, LogOut, User, Settings, Award, Trophy, CreditCard, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";

const studentNavItems = [
  { name: "Discover", path: "/discover", icon: Compass },
  { name: "My Pass", path: "/my-pass", icon: Ticket },
  { name: "Passport", path: "/passport", icon: Award },
  { name: "Rankings", path: "/leaderboards", icon: Trophy },
];

export const Topbar = ({ 
  variant = "org", 
  showStudentNav = false 
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, organization, signOut } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([
    // Demo notifications - can be removed or replaced with real data
  ]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const dismissNotification = (index) => {
    setNotifications(prev => prev.filter((_, i) => i !== index));
  };

  // Get display name and initials
  const displayName = profile?.display_name || user?.email?.split('@')[0] || 'User';
  const initials = displayName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  // Context label based on variant
  const contextLabel = variant === 'org' 
    ? organization?.name || 'Organization' 
    : variant === 'platform' 
      ? 'CamPassed Platform' 
      : '';
  
  return (
    <header 
      className={cn(
        "sticky top-0 z-30 h-16 flex items-center px-4 lg:px-6 bg-graphite/80 backdrop-blur-xl border-b border-white/[0.08]",
        variant === "org" || variant === "platform" ? "lg:pl-[264px]" : ""
      )}
      data-testid="topbar"
    >
      <div className="flex items-center justify-between w-full">
        {/* Left side */}
        <div className="flex items-center gap-4">
          {/* Logo for student/auth layouts */}
          {(variant === "student" || variant === "auth") && (
            <Link to="/" className="flex items-center gap-2 mr-4">
              <img 
                src="/assets/app-icon.png" 
                alt="CamPassed" 
                className="w-8 h-8 rounded-lg shadow-lg shadow-electric-ice-blue/20"
              />
              <span className="text-xl font-bold tracking-[-0.02em] text-cloud-white hidden sm:block">
                CamPassed
              </span>
            </Link>
          )}
          
          {/* Context label */}
          {(variant === "org" || variant === "platform") && contextLabel && (
            <div className="hidden sm:flex items-center gap-2 ml-12 lg:ml-0">
              <span className="text-soft-silver text-sm">{contextLabel}</span>
            </div>
          )}
          
          {/* Student navigation */}
          {showStudentNav && (
            <nav className="flex items-center gap-1 ml-4">
              {studentNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`student-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={cn(
                      "flex items-center gap-2 px-4 py-2 text-sm rounded-md transition-all duration-200",
                      isActive
                        ? "bg-white/[0.08] text-cloud-white"
                        : "text-soft-silver hover:bg-white/[0.03] hover:text-cloud-white"
                    )}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{item.name}</span>
                  </Link>
                );
              })}
            </nav>
          )}
        </div>
        
        {/* Right side */}
        <div className="flex items-center gap-2">
          {/* Notification Bell */}
          <DropdownMenu open={showNotifications} onOpenChange={setShowNotifications}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-soft-silver hover:text-cloud-white hover:bg-white/[0.05] relative"
                data-testid="notification-bell"
              >
                <Bell className="w-5 h-5" />
                {notifications.length > 0 && (
                  <span className="absolute top-2 right-2 w-2 h-2 bg-signal-red rounded-full" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              className="w-80 bg-graphite border-white/[0.08] text-cloud-white"
              align="end"
            >
              <DropdownMenuLabel className="flex items-center justify-between">
                <span>Notifications</span>
                {notifications.length > 0 && (
                  <span className="text-xs text-muted-text">{notifications.length} new</span>
                )}
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/[0.08]" />
              {notifications.length === 0 ? (
                <div className="p-4 text-center text-muted-text text-sm">
                  No new notifications
                </div>
              ) : (
                notifications.map((notif, index) => (
                  <DropdownMenuItem 
                    key={index}
                    className="flex items-start gap-3 p-3 cursor-default hover:bg-white/[0.05] focus:bg-white/[0.05]"
                  >
                    <div className="flex-1">
                      <p className="text-sm font-medium">{notif.title}</p>
                      <p className="text-xs text-muted-text mt-0.5">{notif.message}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      onClick={(e) => { e.stopPropagation(); dismissNotification(index); }}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Quick Action */}
          {(variant === "org" || variant === "platform") && (
            <Button
              size="sm"
              className="hidden sm:flex bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90 font-medium transition-all duration-200 hover:shadow-lg hover:shadow-electric-ice-blue/20"
              data-testid="quick-action-btn"
              onClick={() => navigate(variant === "org" ? "/app/events" : "/platform/campuses")}
            >
              <Plus className="w-4 h-4 mr-1" />
              {variant === "org" ? "New Event" : "Add Campus"}
            </Button>
          )}
          
          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="relative h-9 w-9 rounded-full hover:bg-white/[0.05]"
                data-testid="user-menu-trigger"
              >
                <Avatar className="h-8 w-8">
                  {profile?.avatar_url && (
                    <AvatarImage src={profile.avatar_url} alt={displayName} />
                  )}
                  <AvatarFallback className="bg-obsidian text-soft-silver text-sm border border-white/[0.08]">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              className="w-56 bg-graphite border-white/[0.08] text-cloud-white"
              align="end"
            >
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium">{displayName}</p>
                  <p className="text-xs text-soft-silver">{user?.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/[0.08]" />
              <DropdownMenuItem 
                className="cursor-pointer hover:bg-white/[0.05] focus:bg-white/[0.05]"
                data-testid="menu-profile"
                onClick={() => navigate(variant === 'platform' ? '/platform/profile' : variant === 'student' ? '/passport' : '/app/profile')}
              >
                <User className="w-4 h-4 mr-2" />
                Profile
              </DropdownMenuItem>
              {variant === 'student' && (
                <DropdownMenuItem 
                  className="cursor-pointer hover:bg-white/[0.05] focus:bg-white/[0.05]"
                  data-testid="menu-billing"
                  onClick={() => navigate('/student/billing')}
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Billing
                </DropdownMenuItem>
              )}
              {variant !== 'student' && (
                <DropdownMenuItem 
                  className="cursor-pointer hover:bg-white/[0.05] focus:bg-white/[0.05]"
                  data-testid="menu-settings"
                  onClick={() => navigate(variant === 'platform' ? '/platform/settings' : '/app/settings')}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator className="bg-white/[0.08]" />
              <DropdownMenuItem 
                className="cursor-pointer text-signal-red hover:bg-white/[0.05] focus:bg-white/[0.05]"
                data-testid="menu-logout"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default Topbar;
